import React, { useState } from 'react';
import { Box, Button, Typography, Avatar, CircularProgress } from '@mui/material';
import { PhotoCamera } from '@mui/icons-material';
import { FirebaseService } from '../../../services/FirebaseService';

interface ProfileImageEditorProps {
  currentImageUrl?: string;
  initials: string;
  size: number;
  onImageUpdate: (url: string) => void;
}

export const ProfileImageEditor: React.FC<ProfileImageEditorProps> = ({ 
  currentImageUrl, 
  initials, 
  size, 
  onImageUpdate 
}) => {
  const [uploading, setUploading] = useState(false);
  const [imageUrl, setImageUrl] = useState(currentImageUrl);
  const [imageError, setImageError] = useState(false);

  // Update imageUrl when currentImageUrl prop changes
  React.useEffect(() => {
    setImageUrl(currentImageUrl);
    setImageError(false);
  }, [currentImageUrl]);

  const handleImageError = () => {
    setImageError(true);
  };

  const handleImageLoad = () => {
    setImageError(false);
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      console.error('Please select a valid image file');
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      console.error('File size must be less than 5MB');
      return;
    }

    setUploading(true);
    try {
      // Upload to Firebase Storage
      const { url: downloadUrl } = await FirebaseService.uploadProfileImage(file);
      
      // Update profile with new image URL
      await FirebaseService.updateUserProfile({ profileImageUrl: downloadUrl });
      
      // Update local state with timestamp to force refresh (iPhone app pattern)
      const timestampedUrl = `${downloadUrl}?t=${Date.now()}`;
      setImageUrl(timestampedUrl);
      setImageError(false); // Reset error state
      onImageUpdate(timestampedUrl);
      
      console.log('Profile image updated successfully!');
    } catch (error) {
      console.error('Upload error:', error);
      console.error('Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const getInitials = () => {
    return initials || 'U';
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
      <Avatar
        src={imageUrl && !imageError ? imageUrl : undefined}
        key={imageUrl} // Force refresh on URL change (like iPhone AsyncImage with .id())
        sx={{ 
          width: size, 
          height: size, 
          fontSize: `${size / 3}px`,
          backgroundColor: (!imageUrl || imageError) ? 'white' : 'transparent',
          color: (!imageUrl || imageError) ? '#666' : 'white',
          border: '2px solid #e0e0e0'
        }}
        onError={handleImageError}
        onLoad={handleImageLoad}
      >
        {(!imageUrl || imageError) && getInitials()}
      </Avatar>
      
      <input
        accept="image/*"
        style={{ display: 'none' }}
        id="profile-image-upload"
        type="file"
        onChange={handleFileUpload}
        disabled={uploading}
      />
      
      <label htmlFor="profile-image-upload">
        <Button
          variant="contained"
          component="span"
          startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : <PhotoCamera />}
          disabled={uploading}
          sx={{ 
            minWidth: 140,
            backgroundColor: '#2E7D4D', // Match your CSS accent-green
            '&:hover': {
              backgroundColor: 'rgba(46, 125, 77, 0.9)' // Slightly darker on hover
            }
          }}
        >
          {uploading ? 'Uploading...' : 'CHANGE PHOTO'}
        </Button>
      </label>
      
      <Typography variant="caption" color="text.secondary" align="center">
        Supported formats: JPG, PNG, HEIC, WEBP<br />
        Maximum size: 5MB
      </Typography>
    </Box>
  );
};
